// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module createRealtimeStats
 * @package @geenius/analytics/solidjs
 * @description Polls realtime analytics data for the SolidJS variant. The
 * primitive manages loading state and interval cleanup for widgets that need
 * continuously refreshed traffic summaries.
 */

import type { RealtimeStats } from "@geenius/analytics";
import { DEFAULT_REALTIME_INTERVAL } from "@geenius/analytics";
import { createSignal, onCleanup, onMount } from "solid-js";

/**
 * Realtime polling state returned by {@link createRealtimeStats}.
 *
 * @property stats Signal accessor for the latest realtime statistics snapshot.
 * @property isLoading Signal accessor for the initial loading state.
 * @property error Signal accessor for the latest polling error.
 */
export interface CreateRealtimeStatsReturn {
  stats: () => RealtimeStats | null;
  isLoading: () => boolean;
  error: () => Error | null;
}

function normalizeRealtimeStatsError(err: unknown): Error {
  if (err instanceof Error) {
    return err;
  }

  return new Error(
    typeof err === "string" ? err : "Realtime stats polling failed",
  );
}

/**
 * Polls realtime analytics statistics on a fixed interval.
 *
 * @param fetcher Async function that returns the current realtime statistics snapshot.
 * @param interval Poll interval in milliseconds. Defaults to the shared realtime interval.
 * @returns Reactive realtime stats state for the polling lifecycle.
 */
export function createRealtimeStats(
  fetcher: () => Promise<RealtimeStats>,
  interval = DEFAULT_REALTIME_INTERVAL,
): CreateRealtimeStatsReturn {
  const [stats, setStats] = createSignal<RealtimeStats | null>(null);
  const [isLoading, setIsLoading] = createSignal(true);
  const [error, setError] = createSignal<Error | null>(null);
  let timer: ReturnType<typeof setInterval> | undefined;

  onMount(() => {
    const poll = async (): Promise<void> => {
      try {
        setStats(await fetcher());
        setError(null);
      } catch (err) {
        setError(normalizeRealtimeStatsError(err));
      } finally {
        setIsLoading(false);
      }
    };

    void poll();
    timer = setInterval(() => {
      void poll();
    }, interval);
  });

  onCleanup(() => {
    if (timer) {
      clearInterval(timer);
    }
  });

  return { stats, isLoading, error };
}
